import { FC, memo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { userApi } from 'api_entities/user';
import { UserProfile } from 'api_entities/user/types';
import { CACHE_KEYS } from 'constants/cache';
import { ContentWrapper } from 'src/common';

import GeneralInfo from './GeneralInfo';
import SocialAccounts from './SocialAccounts';

import { Container, Inner, Grid, TabButton, TabButtonWrapper } from './ProfileForm.styles';

export type ProfileFormProps = {
  profile?: UserProfile;
};

const ProfileForm: FC<ProfileFormProps> = ({ profile }) => {
  const [activeTab, setActiveTab] = useState<number>(1);

  const queryClient = useQueryClient();
  const { mutate: submitForm, isLoading } = useMutation(CACHE_KEYS.USER, userApi.postUser, {
    onSuccess: async () => {
      await queryClient.refetchQueries<UserProfile>(CACHE_KEYS.USER);
    },
    onSettled: (data) => {
      const profile = queryClient.getQueryData<UserProfile>(CACHE_KEYS.USER);

      if (profile) {
        const update = Object.assign({ ...profile }, data);
        queryClient.setQueryData(CACHE_KEYS.USER, update);
      }
    },
  });

  const handleFormSubmit = (values: any) => {
    submitForm(values);
  };

  return (
    <ContentWrapper>
      <Container>
        <Inner>
          <Grid>
            <TabButtonWrapper>
              <TabButton isActive={activeTab === 1} onClick={() => setActiveTab(1)}>
                Profile
              </TabButton>
              <TabButton isActive={activeTab === 2} onClick={() => setActiveTab(2)}>
                Social Accounts
              </TabButton>
            </TabButtonWrapper>
            {activeTab === 1 ? (
              <GeneralInfo
                firstName={profile?.firstName}
                lastName={profile?.lastName}
                city={profile?.city}
                about={profile?.about}
                levelOfStudy={profile?.levelOfStudy}
                studySubject={profile?.studySubject}
                phoneNumber={profile?.phoneNumber}
                startStudy={profile?.startStudy}
                endStudy={profile?.endStudy}
                consent={profile?.consent}
                onSubmit={handleFormSubmit}
                loading={isLoading}
              />
            ) : null}
            {activeTab === 2 ? (
              <SocialAccounts
                twitter={profile?.twitter}
                facebook={profile?.facebook}
                instagram={profile?.instagram}
                medium={profile?.medium}
                linkedin={profile?.linkedin}
                onSubmit={handleFormSubmit}
                loading={isLoading}
              />
            ) : null}
          </Grid>
        </Inner>
      </Container>
    </ContentWrapper>
  );
};

export default memo(ProfileForm);
